import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Key, User, CheckCircle, AlertCircle, ExternalLink, Trash2, Plus, ChevronDown, ChevronRight, Check, Shield, Lock, Copy, Download, Github, FileArchive, AlertTriangle, Loader2, CreditCard, Terminal, ArrowRight, Layers, Zap, RotateCcw } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';

const TIER_COLORS = { free: '#22C55E', budget: '#00D4FF', standard: '#F2A900', premium: '#FF4D00', economy: '#22C55E' };
const TIER_ORDER = ['free', 'budget', 'standard', 'premium'];

const PROVIDERS = [
  {
    id: 'openrouter',
    name: 'OpenRouter',
    description: 'Access 300+ models from all providers through a single key.',
    url: 'https://openrouter.ai/keys',
    placeholder: 'sk-or-v1-...',
    color: '#FF4D00',
  },
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'Direct access to GPT-4, GPT-4o, o1, o3, and other OpenAI models.',
    url: 'https://platform.openai.com/api-keys',
    placeholder: 'sk-...',
    color: '#22C55E',
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    description: 'Direct access to Claude Sonnet, Opus, and Haiku models.',
    url: 'https://console.anthropic.com/settings/keys',
    placeholder: 'sk-ant-...',
    color: '#A855F7',
  },
  {
    id: 'github',
    name: 'GitHub',
    description: 'Connect your repos to provide code context and commit Fray results.',
    url: 'https://github.com/settings/tokens',
    placeholder: 'ghp_...',
    color: '#F5F5F5',
  },
  {
    id: 'perplexity',
    name: 'Perplexity',
    description: 'Web search and research for Fray context (Pro plan feature).',
    url: 'https://www.perplexity.ai/settings/api',
    placeholder: 'pplx-...',
    color: '#06B6D4',
  },
  {
    id: 'exa',
    name: 'Exa',
    description: 'Semantic search for deep research context (Pro plan feature).',
    url: 'https://dashboard.exa.ai/api-keys',
    placeholder: 'exa-...',
    color: '#3B82F6',
  },
];

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'api-keys');
  const [tierFeatures, setTierFeatures] = useState({});

  useEffect(() => {
    if (!user) return;
    API.get('/api/my-tier')
      .then(r => setTierFeatures(r.data.features || {}))
      .catch(() => {});
  }, [user?.plan]);

  const mcpAccess = tierFeatures.mcp_access;
  const showMcpTab = mcpAccess && mcpAccess !== false && mcpAccess !== 'none';
  const showApiKeysTab = tierFeatures.api_access !== false;
  // #483: Hide Plan tab from workspace users who aren't owner/admin
  const wsRole = user?.workspace_role;
  const canManageBilling = !wsRole || ['owner', 'admin', 'account_super_admin', 'account_admin'].includes(wsRole);

  const tabs = ['profile', ...(canManageBilling ? ['plan'] : []), 'api-keys', 'models', 'github', 'usage', 'security', 'privacy'];
  if (showMcpTab) tabs.splice(canManageBilling ? 4 : 3, 0, 'mcp-setup');

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-3xl mx-auto">
        <h1 className="text-xl md:text-2xl font-bold font-['Barlow_Condensed'] mb-6" data-testid="settings-title">Settings</h1>

        <div className="flex gap-1 mb-6 border-b border-[#2A2A2A] overflow-x-auto">
          {tabs.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 md:px-4 py-2 min-h-[44px] text-xs font-mono transition-colors border-b-2 -mb-px whitespace-nowrap ${
                tab === t ? 'text-[#FF4D00] border-[#FF4D00]' : 'text-[#A0A0A0] border-transparent hover:text-[#F5F5F5]'
              }`}
              data-testid={`settings-tab-${t}`}
            >{{ 'mcp-setup': 'MCP Setup', 'api-keys': 'API Keys', 'models': 'Available Models' }[t] || t.replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}</button>
          ))}
        </div>

        {tab === 'api-keys' && <APIKeysTab user={user} refreshUser={refreshUser} canManageBilling={canManageBilling} />}
        {tab === 'models' && <AvailableModelsTab user={user} />}
        {tab === 'plan' && <PlanTab user={user} />}
        {tab === 'mcp-setup' && <McpSetupTab user={user} />}
        {tab === 'github' && <GitHubTab user={user} refreshUser={refreshUser} />}
        {tab === 'usage' && <UsageTab />}
        {tab === 'security' && <SecurityTab user={user} refreshUser={refreshUser} />}
        {tab === 'privacy' && <PrivacyTab user={user} />}
        {tab === 'profile' && <ProfileTab user={user} refreshUser={refreshUser} />}
      </div>
    </div>
  );
}

function APIKeysTab({ user, refreshUser, canManageBilling }) {
  // #485: Enterprise workspace users (non-admin) can only manage GitHub (personal)
  const isEnterpriseUser = user?.workspace_role && !canManageBilling;
  const ENTERPRISE_MANAGED = ['openrouter', 'openai', 'anthropic', 'perplexity', 'exa'];
  const [configuredKeys, setConfiguredKeys] = useState({});
  const [loading, setLoading] = useState(true);
  const [defaultModel, setDefaultModel] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [modelSearch, setModelSearch] = useState('');

  useEffect(() => {
    loadKeys();
    loadDefaultModel();
    loadModels();
  }, []);

  const loadKeys = async () => {
    try {
      const res = await API.get('/api/keys');
      setConfiguredKeys(res.data.providers || {});
    } catch {}
    setLoading(false);
  };

  const loadDefaultModel = async () => {
    try {
      const res = await API.get('/api/settings/default-model');
      setDefaultModel(res.data.default_model || '');
    } catch {}
  };

  const loadModels = async () => {
    try {
      const res = await API.get('/api/byok/models');
      setAvailableModels(res.data.models || []);
    } catch {}
  };

  const saveDefaultModel = async (modelId) => {
    setDefaultModel(modelId);
    setShowModelPicker(false);
    setModelSearch('');
    try {
      await API.put('/api/settings/default-model', { model_id: modelId });
    } catch {}
  };

  const filteredModels = availableModels.filter(m =>
    !modelSearch || m.id.toLowerCase().includes(modelSearch.toLowerCase()) || m.name.toLowerCase().includes(modelSearch.toLowerCase())
  );

  return (
    <div data-testid="api-keys-tab">
      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] mb-6">
        <div className="flex items-start gap-3">
          <Key className="w-5 h-5 text-[#FF4D00] shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-1">API Keys</h3>
            <p className="text-xs text-[#A0A0A0] leading-relaxed">
              Add keys for one or more providers. Each Voice in a Fray can use a different model —
              the system routes to the correct provider automatically based on the model you select.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {PROVIDERS.map(provider => {
          const isManaged = isEnterpriseUser && ENTERPRISE_MANAGED.includes(provider.id);
          if (isManaged) {
            return (
              <div key={provider.id} className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: configuredKeys[provider.id] ? '#22C55E' : '#A0A0A0' }} />
                  <div>
                    <span className="text-sm font-mono" style={{ color: provider.color }}>{provider.name}</span>
                    <p className="text-[10px] text-[#A0A0A0] mt-0.5">Managed by your workspace admin</p>
                  </div>
                </div>
                <Lock className="w-4 h-4 text-[#A0A0A0]" />
              </div>
            );
          }
          return (
            <ProviderKeyCard
              key={provider.id}
              provider={provider}
              configured={configuredKeys[provider.id]}
              onUpdate={() => { loadKeys(); loadModels(); refreshUser(); }}
            />
          );
        })}
      </div>

      {/* Default Model Selector */}
      <div className="mt-8 p-4 bg-[#1A1A1A] border border-[#2A2A2A]" data-testid="default-model-section">
        <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-1">Default Model</h3>
        <p className="text-xs text-[#A0A0A0] mb-3">
          Used for system tasks: parsing imported prompts, generating verdicts, and as the fallback when no model is specified.
        </p>
        <div className="relative">
          <button
            onClick={() => setShowModelPicker(!showModelPicker)}
            className={`w-full flex items-center justify-between px-3 py-2.5 border text-left transition-all ${
              showModelPicker ? 'border-[#FF4D00] bg-[#FF4D00]/5' : 'border-[#2A2A2A] hover:border-[#FF4D00]/50'
            }`}
            data-testid="default-model-selector"
          >
            <span className="text-xs font-mono text-[#F5F5F5]">
              {defaultModel || 'Auto (best available)'}
            </span>
            <ChevronDown className={`w-4 h-4 text-[#A0A0A0] transition-transform ${showModelPicker ? 'rotate-180' : ''}`} />
          </button>

          {showModelPicker && (
            <div className="absolute z-50 left-0 right-0 mt-1 bg-[#1A1A1A] border border-[#2A2A2A] shadow-xl max-h-[280px] flex flex-col">
              <div className="p-2 border-b border-[#2A2A2A]">
                <input
                  type="text" value={modelSearch} onChange={e => setModelSearch(e.target.value)}
                  className="w-full px-3 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-xs focus:outline-none focus:border-[#FF4D00]"
                  placeholder="Search models..." autoFocus
                  data-testid="default-model-search"
                />
              </div>
              <div className="overflow-y-auto flex-1">
                <button onClick={() => saveDefaultModel('')}
                  className={`w-full px-3 py-2 text-left text-xs border-b border-[#0F0F0F] ${!defaultModel ? 'bg-[#FF4D00]/10' : 'hover:bg-[#2A2A2A]/50'}`}>
                  <span className="text-[#FF4D00]">Auto</span>
                  <span className="text-[#A0A0A0] ml-2">— best available model</span>
                </button>
                {filteredModels.slice(0, 40).map(m => (
                  <button key={m.id} onClick={() => saveDefaultModel(m.id)}
                    className={`w-full flex items-center justify-between px-3 py-1.5 text-left border-b border-[#0F0F0F] ${defaultModel === m.id ? 'bg-[#FF4D00]/10' : 'hover:bg-[#2A2A2A]/50'}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[8px] w-10 text-center px-1 py-0.5 border font-mono shrink-0"
                        style={{ color: TIER_COLORS[m.tier], borderColor: TIER_COLORS[m.tier] + '40' }}>{m.tier?.toUpperCase().slice(0,4)}</span>
                      <span className="text-[10px] text-[#F5F5F5] truncate">{m.name || m.id}</span>
                    </div>
                    {defaultModel === m.id && <Check className="w-3 h-3 text-[#FF4D00] shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProviderKeyCard({ provider, configured, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [key, setKey] = useState('');
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [modelCount, setModelCount] = useState(0);

  const isConfigured = configured?.configured;

  const handleValidate = async () => {
    if (!key.trim()) return;
    setValidating(true);
    setError('');
    setSuccess(false);
    try {
      if (provider.id === 'github') {
        // GitHub uses special connect endpoint
        const res = await API.post('/api/github/connect', { token: key });
        setModelCount(0);
        setSuccess(true);
        setKey('');
        setExpanded(false);
        onUpdate();
      } else if (provider.id === 'perplexity' || provider.id === 'exa') {
        const res = await API.post('/api/keys/research/validate', { provider: provider.id, api_key: key });
        setModelCount(0);
        setSuccess(true);
        setKey('');
        setExpanded(false);
        onUpdate();
      } else {
        const res = await API.post('/api/keys/validate', { provider: provider.id, api_key: key });
        setModelCount(res.data.total || 0);
        setSuccess(true);
        setKey('');
        setExpanded(false);
        onUpdate();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Validation failed');
    }
    setValidating(false);
  };

  const handleRemove = async () => {
    try {
      await API.delete(`/api/keys/${provider.id}`);
      setSuccess(false);
      onUpdate();
    } catch {}
  };

  return (
    <div className={`border transition-colors ${isConfigured ? 'border-[#2A2A2A]' : 'border-[#2A2A2A]'} bg-[#1A1A1A]`}
      data-testid={`provider-card-${provider.id}`}>
      <div className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 flex items-center justify-center border-2 text-xs font-bold font-mono shrink-0"
              style={{ borderColor: provider.color, color: provider.color }}>
              {provider.name.charAt(0)}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium font-['Barlow_Condensed'] text-sm">{provider.name}</span>
                {isConfigured && (
                  <span className="text-[9px] px-1.5 py-0.5 bg-[#22C55E]/10 text-[#22C55E] border border-[#22C55E]/30 font-mono">ACTIVE</span>
                )}
              </div>
              <p className="text-[10px] text-[#A0A0A0] mt-0.5 line-clamp-2">{provider.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-11 sm:ml-0">
            {isConfigured && (
              <>
                <span className="text-[9px] font-mono text-[#A0A0A0] hidden sm:inline">{configured.key_hint}</span>
                <button onClick={handleRemove} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#A0A0A0] hover:text-[#EF4444] transition-colors" data-testid={`remove-${provider.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
            <button
              onClick={() => setExpanded(!expanded)}
              className={`px-3 py-2 min-h-[44px] text-xs font-mono border transition-colors ${
                expanded ? 'border-[#FF4D00] text-[#FF4D00]' :
                isConfigured ? 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#FF4D00]/40' :
                'border-[#FF4D00]/40 text-[#FF4D00] hover:bg-[#FF4D00]/5'
              }`}
              data-testid={`toggle-${provider.id}`}
            >
              {isConfigured ? 'Update' : 'Add Key'}
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="border-t border-[#2A2A2A] p-4">
          {error && (
            <div className="p-2 mb-3 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-xs flex items-center gap-2" data-testid={`error-${provider.id}`}>
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="password"
              value={key}
              onChange={e => setKey(e.target.value)}
              className="flex-1 px-3 py-3 min-h-[44px] bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono text-[#F5F5F5] focus:outline-none focus:border-[#FF4D00]"
              placeholder={provider.placeholder}
              data-testid={`key-input-${provider.id}`}
            />
            <button
              onClick={handleValidate}
              disabled={validating || !key.trim()}
              className="px-4 py-3 min-h-[44px] bg-[#FF4D00] text-black text-xs font-medium disabled:opacity-50"
              data-testid={`validate-${provider.id}`}
            >
              {validating ? 'Validating...' : 'Validate'}
            </button>
          </div>

          <div className="flex items-center justify-between mt-2">
            <a href={provider.url} target="_blank" rel="noreferrer" className="text-[10px] text-[#FF4D00] hover:underline flex items-center gap-1">
              Get a key at {provider.name} <ExternalLink className="w-3 h-3" />
            </a>
            {success && (
              <span className="text-[10px] text-[#22C55E] flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> {modelCount} models validated
              </span>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}

function GitHubTab({ user, refreshUser }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const r = await API.get('/api/github/status');
      setStatus(r.data);
    } catch {
      setStatus({ connected: false });
    }
    setLoading(false);
  };

  const handleConnect = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    window.location.href = `${backendUrl}/api/auth/github?connect=true`;
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await API.post('/api/github/disconnect');
      setStatus({ connected: false });
      if (refreshUser) refreshUser();
    } catch {}
    setDisconnecting(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-6 h-6 border-2 border-[#FF4D00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="github-tab">
      <div className="p-6 bg-[#1A1A1A] border border-[#2A2A2A] mb-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[#F5F5F5]/10 flex items-center justify-center">
            <Github className="w-5 h-5 text-[#F5F5F5]" />
          </div>
          <div>
            <h3 className="text-sm font-semibold font-['Barlow_Condensed']">GitHub Integration</h3>
            <p className="text-[10px] text-[#A0A0A0]">Connect repos, attach code context, commit fray results</p>
          </div>
        </div>

        {status?.connected ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-[#0F0F0F] border border-[#22C55E]/20">
              {status.avatar_url && (
                <img src={status.avatar_url} alt="" className="w-8 h-8 rounded-full" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{status.username}</span>
                  <span className="text-[9px] px-1.5 py-0.5 bg-[#22C55E]/10 text-[#22C55E] font-mono">CONNECTED</span>
                </div>
                {status.connected_at && (
                  <span className="text-[10px] text-[#A0A0A0] font-mono">
                    Connected {new Date(status.connected_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>

            {status.default_repo && (
              <div className="p-3 bg-[#0F0F0F] border border-[#2A2A2A]">
                <span className="text-[10px] text-[#A0A0A0] font-mono block mb-1">DEFAULT REPO</span>
                <span className="text-sm text-[#FF4D00]">{status.default_repo}</span>
              </div>
            )}

            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="px-4 py-2 min-h-[44px] border border-[#EF4444]/30 text-sm text-[#EF4444] hover:bg-[#EF4444]/5 transition-colors disabled:opacity-50"
              data-testid="disconnect-github-btn"
            >
              {disconnecting ? 'Disconnecting...' : 'Disconnect GitHub'}
            </button>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-[#A0A0A0] mb-4">
              Connect your GitHub account to browse repos, attach code as context, and commit fray results.
            </p>
            <button
              onClick={handleConnect}
              className="px-6 py-3 min-h-[44px] bg-[#F5F5F5] text-black text-sm font-medium flex items-center gap-2 mx-auto hover:bg-[#F5F5F5]/90 transition-colors"
              data-testid="connect-github-btn"
            >
              <Github className="w-4 h-4" /> Connect GitHub
            </button>
          </div>
        )}
      </div>

      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
        <h4 className="text-xs font-mono text-[#A0A0A0] mb-2">WHAT YOU CAN DO</h4>
        <ul className="space-y-2 text-xs text-[#A0A0A0]">
          <li className="flex items-start gap-2"><span className="text-[#FF4D00]">1.</span> Attach code files as context when creating a Fray</li>
          <li className="flex items-start gap-2"><span className="text-[#FF4D00]">2.</span> Commit Fray verdicts and transcripts directly to your repos</li>
          <li className="flex items-start gap-2"><span className="text-[#FF4D00]">3.</span> Browse and select files from any connected repository</li>
        </ul>
      </div>
    </div>
  );
}

function ProfileTab({ user, refreshUser }) {
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put('/api/settings/profile', { name });
      await refreshUser();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
    setSaving(false);
  };

  return (
    <div data-testid="profile-tab">
      <div className="space-y-4">
        <div>
          <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Name</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)}
            className="w-full px-4 py-2.5 bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00]" data-testid="profile-name" />
        </div>
        <div>
          <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Email</label>
          <input type="email" value={user?.email || ''} readOnly
            className="w-full px-4 py-2.5 bg-[#1A1A1A] border border-[#2A2A2A] text-sm text-[#A0A0A0]" data-testid="profile-email" />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <div>
            <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Plan</label>
            <span className="text-sm font-mono capitalize">{user?.plan || 'free'}</span>
          </div>
          <div>
            <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Billing Mode</label>
            <span className="text-sm font-mono text-[#FF4D00]">BYOK</span>
            <span className="text-[9px] text-[#A0A0A0] block mt-0.5">Managed billing with Fray credits coming soon</span>
          </div>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="px-6 py-3 min-h-[44px] bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          data-testid="save-profile-btn">
          {saved ? <><CheckCircle className="w-4 h-4" /> Saved</> : saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}


function SecurityTab({ user, refreshUser }) {
  const [status, setStatus] = useState(null); // { enabled, enabled_at }
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('idle'); // idle | setup | verify | recovery | disable | regen
  const [qrData, setQrData] = useState(null); // { qr_code, secret, temp_token }
  const [verifyCode, setVerifyCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [regenCode, setRegenCode] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const res = await API.get('/api/auth/2fa/status');
      setStatus(res.data);
    } catch {
      setStatus({ enabled: false });
    }
    setLoading(false);
  };

  const handleSetup = async () => {
    setError('');
    setActionLoading(true);
    try {
      const res = await API.post('/api/auth/2fa/setup');
      setQrData(res.data);
      setStep('setup');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start 2FA setup');
    }
    setActionLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setActionLoading(true);
    try {
      const res = await API.post('/api/auth/2fa/verify', {
        code: verifyCode,
      });
      setRecoveryCodes(res.data.recovery_codes || []);
      setStep('recovery');
      setVerifyCode('');
      await loadStatus();
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    }
    setActionLoading(false);
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setError('');
    setActionLoading(true);
    try {
      await API.delete('/api/auth/2fa', { data: { code: disableCode } });
      setStep('idle');
      setDisableCode('');
      await loadStatus();
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to disable 2FA');
    }
    setActionLoading(false);
  };

  const handleRegenerate = async (e) => {
    e.preventDefault();
    setError('');
    setActionLoading(true);
    try {
      const res = await API.post('/api/auth/2fa/recovery-codes/regenerate', { code: regenCode });
      setRecoveryCodes(res.data.recovery_codes || []);
      setStep('recovery');
      setRegenCode('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to regenerate recovery codes');
    }
    setActionLoading(false);
  };

  const copyRecoveryCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadRecoveryCodes = () => {
    const text = `Fray Recovery Codes\nGenerated: ${new Date().toISOString()}\n\n${recoveryCodes.join('\n')}\n\nEach code can only be used once.`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'fray-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="animate-shimmer h-40" />;

  // Recovery codes display (shown after enable or regenerate)
  if (step === 'recovery' && recoveryCodes.length > 0) {
    return (
      <div data-testid="security-tab">
        <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-5 h-5 text-[#22C55E]" />
            <h3 className="text-sm font-semibold font-['Barlow_Condensed']">Recovery Codes</h3>
          </div>

          <div className="p-3 mb-4 bg-[#FF4D00]/10 border border-[#FF4D00]/20">
            <p className="text-xs text-[#FF4D00] font-medium">Save these codes — they can't be retrieved again.</p>
            <p className="text-[10px] text-[#A0A0A0] mt-1">Each code can only be used once to sign in if you lose access to your authenticator app.</p>
          </div>

          <div className="grid grid-cols-2 gap-2 font-mono text-sm mb-4">
            {recoveryCodes.map((code, i) => (
              <div key={i} className="px-3 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-center text-[#F5F5F5]" data-testid={`recovery-code-${i}`}>{code}</div>
            ))}
          </div>

          <div className="flex gap-2">
            <button onClick={copyRecoveryCodes}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-[#2A2A2A] text-xs font-mono text-[#F5F5F5] hover:border-[#FF4D00]/40"
              data-testid="copy-recovery-codes">
              <Copy className="w-3.5 h-3.5" /> {copied ? 'Copied!' : 'Copy All'}
            </button>
            <button onClick={downloadRecoveryCodes}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-[#2A2A2A] text-xs font-mono text-[#F5F5F5] hover:border-[#FF4D00]/40"
              data-testid="download-recovery-codes">
              <Download className="w-3.5 h-3.5" /> Download
            </button>
          </div>

          <button onClick={() => { setStep('idle'); setRecoveryCodes([]); }}
            className="w-full mt-4 py-2 bg-[#FF4D00] text-black text-sm font-medium"
            data-testid="recovery-done">
            I've saved my codes
          </button>
        </div>
      </div>
    );
  }

  // Setup flow: show QR code
  if (step === 'setup') {
    return (
      <div data-testid="security-tab">
        <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-5 h-5 text-[#FF4D00]" />
            <h3 className="text-sm font-semibold font-['Barlow_Condensed']">Set Up Two-Factor Authentication</h3>
          </div>

          <p className="text-xs text-[#A0A0A0] mb-4">
            Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.)
          </p>

          {qrData?.qr_code && (
            <div className="flex justify-center mb-4">
              <img src={qrData.qr_code} alt="Scan with authenticator app" className="w-48 h-48" data-testid="2fa-qr-code" />
            </div>
          )}

          {qrData?.secret && (
            <div className="p-3 mb-4 bg-[#0F0F0F] border border-[#2A2A2A]">
              <p className="text-[10px] text-[#A0A0A0] mb-1">Can't scan? Enter this key manually:</p>
              <p className="text-xs font-mono text-[#F5F5F5] text-center tracking-wider select-all" data-testid="2fa-secret">{qrData.secret}</p>
            </div>
          )}

          {error && <div className="p-3 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-xs">{error}</div>}

          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Verification Code</label>
              <input
                type="text"
                value={verifyCode}
                onChange={e => {
                  const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                  setVerifyCode(val);
                }}
                className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#2A2A2A] text-sm font-mono text-center tracking-widest focus:outline-none focus:border-[#FF4D00]"
                placeholder="000000"
                required
                maxLength={6}
                autoFocus
                data-testid="2fa-setup-code"
              />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => { setStep('idle'); setQrData(null); setError(''); }}
                className="flex-1 py-2 border border-[#2A2A2A] text-sm text-[#A0A0A0] hover:text-[#F5F5F5]">
                Cancel
              </button>
              <button type="submit" disabled={actionLoading || verifyCode.length !== 6}
                className="flex-1 py-2 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50"
                data-testid="2fa-setup-verify">
                {actionLoading ? 'Verifying...' : 'Verify & Enable'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Disable 2FA confirmation
  if (step === 'disable') {
    return (
      <div data-testid="security-tab">
        <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
          <div className="flex items-center gap-3 mb-4">
            <Lock className="w-5 h-5 text-[#EF4444]" />
            <h3 className="text-sm font-semibold font-['Barlow_Condensed']">Disable Two-Factor Authentication</h3>
          </div>

          <p className="text-xs text-[#A0A0A0] mb-4">
            Enter a code from your authenticator app to confirm disabling 2FA.
          </p>

          {error && <div className="p-3 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-xs">{error}</div>}

          <form onSubmit={handleDisable} className="space-y-4">
            <div>
              <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Authenticator Code</label>
              <input
                type="text"
                value={disableCode}
                onChange={e => {
                  const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                  setDisableCode(val);
                }}
                className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#2A2A2A] text-sm font-mono text-center tracking-widest focus:outline-none focus:border-[#FF4D00]"
                placeholder="000000"
                required
                maxLength={6}
                autoFocus
                data-testid="2fa-disable-code"
              />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => { setStep('idle'); setError(''); setDisableCode(''); }}
                className="flex-1 py-2 border border-[#2A2A2A] text-sm text-[#A0A0A0] hover:text-[#F5F5F5]">
                Cancel
              </button>
              <button type="submit" disabled={actionLoading || disableCode.length !== 6}
                className="flex-1 py-2 bg-[#EF4444] text-white text-sm font-medium disabled:opacity-50"
                data-testid="2fa-disable-submit">
                {actionLoading ? 'Disabling...' : 'Disable 2FA'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Regenerate recovery codes
  if (step === 'regen') {
    return (
      <div data-testid="security-tab">
        <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-5 h-5 text-[#FF4D00]" />
            <h3 className="text-sm font-semibold font-['Barlow_Condensed']">Regenerate Recovery Codes</h3>
          </div>

          <p className="text-xs text-[#A0A0A0] mb-4">
            This will invalidate your existing recovery codes. Enter a code from your authenticator app to confirm.
          </p>

          {error && <div className="p-3 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-xs">{error}</div>}

          <form onSubmit={handleRegenerate} className="space-y-4">
            <div>
              <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Authenticator Code</label>
              <input
                type="text"
                value={regenCode}
                onChange={e => {
                  const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                  setRegenCode(val);
                }}
                className="w-full px-4 py-2.5 bg-[#0F0F0F] border border-[#2A2A2A] text-sm font-mono text-center tracking-widest focus:outline-none focus:border-[#FF4D00]"
                placeholder="000000"
                required
                maxLength={6}
                autoFocus
                data-testid="2fa-regen-code"
              />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => { setStep('idle'); setError(''); setRegenCode(''); }}
                className="flex-1 py-2 border border-[#2A2A2A] text-sm text-[#A0A0A0] hover:text-[#F5F5F5]">
                Cancel
              </button>
              <button type="submit" disabled={actionLoading || regenCode.length !== 6}
                className="flex-1 py-2 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50"
                data-testid="2fa-regen-submit">
                {actionLoading ? 'Regenerating...' : 'Regenerate Codes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Default: show 2FA status
  return (
    <div data-testid="security-tab">
      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-[#FF4D00] shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-1">Two-Factor Authentication</h3>
            <p className="text-xs text-[#A0A0A0] leading-relaxed">
              Add an extra layer of security to your account by requiring a code from an authenticator app when signing in.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4 p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
        {status?.enabled ? (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-2">
                <span className="text-[9px] px-1.5 py-0.5 bg-[#22C55E]/10 text-[#22C55E] border border-[#22C55E]/30 font-mono" data-testid="2fa-enabled-badge">2FA ENABLED</span>
              </div>
              {status.enabled_at && (
                <span className="text-[10px] text-[#A0A0A0] font-mono">since {new Date(status.enabled_at).toLocaleDateString()}</span>
              )}
            </div>

            <div className="flex gap-2">
              <button onClick={() => { setStep('regen'); setError(''); }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-[#2A2A2A] text-xs font-mono text-[#F5F5F5] hover:border-[#FF4D00]/40"
                data-testid="2fa-regen-btn">
                <Key className="w-3.5 h-3.5" /> Regenerate Recovery Codes
              </button>
              <button onClick={() => { setStep('disable'); setError(''); }}
                className="flex items-center justify-center gap-2 px-4 py-2 border border-[#EF4444]/30 text-xs font-mono text-[#EF4444] hover:bg-[#EF4444]/5"
                data-testid="2fa-disable-btn">
                <Lock className="w-3.5 h-3.5" /> Disable
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-3">
              <Lock className="w-5 h-5 text-[#A0A0A0]" />
              <div>
                <h4 className="text-sm font-medium font-['Barlow_Condensed']">Add Extra Security</h4>
                <p className="text-[10px] text-[#A0A0A0] mt-0.5">Protect your account with an authenticator app like Google Authenticator, Authy, or 1Password.</p>
              </div>
            </div>

            {error && <div className="p-3 mb-3 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-xs">{error}</div>}

            <button onClick={handleSetup} disabled={actionLoading}
              className="px-6 py-2 bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              data-testid="2fa-setup-btn">
              <Shield className="w-4 h-4" />
              {actionLoading ? 'Setting up...' : 'Set up two-factor authentication'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function BillingBanner({ banner }) {
  if (!banner) return null;
  const colors = banner.type === 'error'
    ? { bg: '#A61E17/10', border: '#A61E17', text: '#A61E17' }
    : { bg: '#F2A900/10', border: '#F2A900', text: '#F2A900' };
  return (
    <div className={`p-4 border mb-6`} style={{ background: `${colors.bg}`, borderColor: colors.border }} data-testid="billing-banner">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 shrink-0" style={{ color: colors.text }} />
          <p className="text-sm" style={{ color: colors.text }}>{banner.message}</p>
        </div>
        {banner.action_url && (
          <a href={banner.action_url} className="px-3 py-1.5 text-xs font-mono whitespace-nowrap border transition-colors hover:opacity-80"
            style={{ borderColor: colors.border, color: colors.text }}>
            {banner.action_label}
          </a>
        )}
      </div>
    </div>
  );
}


// ===========================================================================
// MCP SETUP TAB — Two-path: Magic Prompt (expanded) + Manual Setup (collapsed)
// ===========================================================================

const MCP_SERVER_URL = 'https://getfray.ai/api/mcp';

const MCP_PLATFORMS = [
  { id: 'claude-desktop', label: 'Claude Desktop', hasOsTabs: true },
  { id: 'claude-code', label: 'Claude Code' },
  { id: 'cursor', label: 'Cursor' },
  { id: 'windsurf', label: 'Windsurf' },
];

const MCP_OS_TABS = [
  { id: 'macos', label: 'macOS', icon: '🍎' },
  { id: 'windows', label: 'Windows', icon: '🪟' },
];

const MCP_TEMPLATE_KEYS = {
  'claude-code': 'mcp_config_claude_code',
  'claude-desktop': 'mcp_config_claude_desktop',
  'cursor': 'mcp_config_cursor',
  'windsurf': 'mcp_config_windsurf',
};

const MCP_VERIFY_PROMPT = `Verify my Fray MCP setup by running these two checks:

1. Run fray_diagnostics — this checks connectivity, database, API keys, and model availability. All sections should show healthy status.

2. Run fray_of_the_day — this runs an actual AI debate on a fun daily topic and confirms the full pipeline is working end-to-end.

Report the results of both. If both complete successfully, Fray MCP is fully operational.`;

function getMcpConfigPath(platform, os) {
  if (platform === 'claude-code') return 'Terminal (project scope)';
  if (platform === 'claude-desktop') {
    return os === 'windows'
      ? '%APPDATA%\\Claude\\claude_desktop_config.json'
      : '~/Library/Application Support/Claude/claude_desktop_config.json';
  }
  if (platform === 'cursor') return '.cursor/mcp.json';
  if (platform === 'windsurf') return '~/.codeium/windsurf/mcp_config.json';
  return '';
}

function getDesktopConfigForOs(os, apiKey, mcpUrl) {
  const key = apiKey || 'YOUR_API_KEY';
  const url = mcpUrl || MCP_SERVER_URL;
  if (os === 'windows') {
    const npxUrl = `${url}?api_key=${key}`;
    return JSON.stringify({
      mcpServers: {
        fray: {
          command: "C:\\\\Users\\\\USERNAME\\\\AppData\\\\Roaming\\\\npm\\\\npx.cmd",
          args: ["-y", "mcp-remote@latest", npxUrl]
        }
      }
    }, null, 2);
  }
  return JSON.stringify({
    mcpServers: {
      fray: {
        command: "npx",
        args: ["-y", "mcp-remote@latest", `${url}?api_key=${key}`]
      }
    }
  }, null, 2);
}

function substituteTemplateVars(template, apiKey, mcpUrl) {
  if (!template) return '';
  return template
    .replace(/\{\{API_KEY\}\}/g, apiKey || 'YOUR_API_KEY')
    .replace(/\{\{MCP_URL\}\}/g, mcpUrl || MCP_SERVER_URL);
}

/* Magic prompt that installs MCP + skill in a single paste (#556).
   Uses JS template literal — apiKey and mcpUrl are substituted at render time. */
function getMcpMagicPrompt(apiKey, mcpUrl) {
  const key = apiKey || 'YOUR_API_KEY';
  const url = mcpUrl || MCP_SERVER_URL;
  return `I need you to set up the Fray MCP server AND the Fray Agent Skill. Please follow these steps in order:

Prerequisites: Desktop Commander must be enabled in your Tools menu before starting. If it's not enabled, enable it now and then paste this prompt again.

Step 1 — Detect OS and locate the config file
Determine whether this is Windows or macOS, then read the Claude Desktop config file:
* Windows: %APPDATA%\\Claude\\claude_desktop_config.json
* macOS: ~/Library/Application Support/Claude/claude_desktop_config.json
Preserve any existing mcpServers entries in the file.

Step 2 — Add the Fray MCP server entry
Merge the following entry into the mcpServers object and write the file back:

{
  "fray": {
    "command": "npx",
    "args": ["-y", "mcp-remote@latest", "${url}?api_key=${key}"]
  }
}

On Windows, if npx is not on PATH, use the full path:
"command": "C:\\\\Users\\\\USERNAME\\\\AppData\\\\Roaming\\\\npm\\\\npx.cmd"

On macOS, if npx is not on PATH, use: /opt/homebrew/bin/npx

Step 3 — Install the Fray Agent Skill
Create the skills directory and download the Committee Design skill:
* macOS/Linux: mkdir -p ~/.claude/skills/fray && curl -H 'Authorization: Bearer ${key}' https://www.getfray.ai/api/skill/committee-design/download -o ~/.claude/skills/fray/SKILL.md
* Windows (PowerShell): New-Item -ItemType Directory -Force -Path "$env:USERPROFILE\\.claude\\skills\\fray"; Invoke-WebRequest -Headers @{'Authorization'='Bearer ${key}'} -Uri 'https://www.getfray.ai/api/skill/committee-design/download' -OutFile "$env:USERPROFILE\\.claude\\skills\\fray\\SKILL.md"
Confirm the file was created and is non-empty by reading the first few lines.

Step 4 — Tell the user to quit and restart
Print exactly: "Setup complete. Please fully quit Claude Desktop now (Cmd+Q on macOS, Alt+F4 on Windows — not just closing the window) and then relaunch it. After it restarts, paste the verify prompt below into a new conversation."

Step 5 — Display the verify prompt
After the restart instruction, output the following verify prompt for the user to copy:

---
Verify my Fray MCP setup by running these two checks:
1. Run fray_diagnostics — this checks connectivity, database, API keys, and model availability. All sections should show healthy status.
2. Run fray_of_the_day — this runs an actual AI debate on a fun daily topic and confirms the full pipeline is working end-to-end.
Report the results of both. If both complete successfully, Fray MCP is fully operational.
---

Do not attempt to call any Fray tools in this conversation — they will not be available until after the restart.
Server name: fray
API Key: ${key}`;
}

function McpSetupTab({ user }) {
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState({});
  const [platform, setPlatform] = useState('claude-desktop');
  const [os, setOs] = useState('macos');
  const [apiKeys, setApiKeys] = useState([]);
  const [showKey, setShowKey] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [expiration, setExpiration] = useState('90d');
  const [copied, setCopied] = useState('');
  const [downloadableSkills, setDownloadableSkills] = useState([]);
  const [setupPath, setSetupPath] = useState('magic');

  useEffect(() => {
    Promise.all([
      API.get('/api/user/mcp-readiness').catch(() => ({ data: { ready: false, plan_includes_mcp: false } })),
      API.get('/api/mcp/setup-templates').catch(() => ({ data: { templates: {} } })),
      API.get('/api/user/mcp-key').catch(() =>
        API.get('/api/developer/keys').catch(() => ({ data: { keys: [] } }))
      ),
    ]).then(async ([readinessRes, templatesRes, keysRes]) => {
      setReadiness(readinessRes.data);
      setTemplates(templatesRes.data.templates || {});
      const keyList = keysRes.data.keys || [];
      setApiKeys(keyList);

      // Auto-regenerate if the key was auto-generated on invite acceptance (#555)
      // so the magic prompt is immediately usable without manual regeneration
      const active = keyList.find(k => k.is_active);
      if (active && active.name && active.name.includes('auto-generated')) {
        setGenerating(true);
        try {
          const res = await API.post('/api/user/mcp-key/regenerate', { expiration });
          setShowKey(res.data.key);
          const refreshRes = await API.get('/api/user/mcp-key').catch(() => API.get('/api/developer/keys'));
          setApiKeys(refreshRes.data.keys || []);
          setReadiness(prev => ({ ...prev, has_mcp_key: true }));
        } catch (err) {
          console.error('Auto-regenerate failed for invite key:', err);
        }
        setGenerating(false);
      }

      setLoading(false);
    });
    API.get('/api/skills?downloadable=true').then(r => setDownloadableSkills(r.data.skills || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const generateKey = async () => {
    setGenerating(true);
    try {
      const res = await API.post('/api/user/mcp-key/regenerate', { expiration });
      setShowKey(res.data.key);
      const keysRes = await API.get('/api/user/mcp-key').catch(() => API.get('/api/developer/keys'));
      setApiKeys(keysRes.data.keys || []);
      setReadiness(prev => ({ ...prev, has_mcp_key: true }));
    } catch {
      try {
        const res = await API.post('/api/developer/keys', { name: 'MCP' });
        setShowKey(res.data.key);
        const keysRes = await API.get('/api/developer/keys');
        setApiKeys(keysRes.data.keys || []);
        setReadiness(prev => ({ ...prev, has_mcp_key: true }));
      } catch { /* ignore */ }
    }
    setGenerating(false);
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  if (loading) return <div className="animate-shimmer h-40" />;

  // Plan doesn't include MCP
  if (!readiness?.plan_includes_mcp) {
    return (
      <div data-testid="mcp-setup-tab">
        <div className="p-6 bg-[#1A1A1A] border border-[#2A2A2A] text-center">
          <Terminal className="w-8 h-8 text-[#A0A0A0] mx-auto mb-3" />
          <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-2">MCP Access Not Available</h3>
          <p className="text-xs text-[#A0A0A0] mb-4">MCP access is available on Pro, Team, and Enterprise plans.</p>
          <a href="/pricing" className="inline-flex items-center gap-1.5 px-4 py-2 min-h-[44px] bg-[#FF4D00] text-[#0A0A0A] text-sm font-bold font-display uppercase tracking-wider hover:bg-[#FF6B33] transition-colors" data-testid="mcp-upgrade-link">
            Upgrade <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    );
  }

  const activeKey = apiKeys.find(k => k.is_active);
  const fullApiKey = showKey || null;
  const displayApiKey = fullApiKey || (activeKey ? `${activeKey.key_prefix}...` : null);
  const keyIsResolved = !!fullApiKey;
  const mcpUrl = MCP_SERVER_URL;

  // Resolve config for current platform + OS — use full key if available
  const configKey = fullApiKey || 'YOUR_API_KEY';
  const templateKey = MCP_TEMPLATE_KEYS[platform];
  let configSnippet;
  if (platform === 'claude-desktop') {
    configSnippet = getDesktopConfigForOs(os, configKey, mcpUrl);
  } else if (templateKey && templates[templateKey]) {
    configSnippet = substituteTemplateVars(templates[templateKey], configKey, mcpUrl);
  } else {
    configSnippet = substituteTemplateVars(templates.mcp_config_claude_desktop || '', configKey, mcpUrl);
  }

  const magicPrompt = getMcpMagicPrompt(configKey, mcpUrl);

  return (
    <div data-testid="mcp-setup-tab" className="space-y-6">

      {/* ═══ Your API Key ═══ */}
      <div className="p-4 bg-[#1E1E1E] border border-[#2A2A2A]">
        <div className="flex items-center gap-2 mb-3">
          <Key className="w-4 h-4 text-[#00D4FF]" />
          <h3 className="text-sm font-semibold font-['Barlow_Condensed']">Your API Key</h3>
        </div>

        <div className="mb-3">
          <label className="text-[10px] font-mono text-[#A0A0A0] mb-1 block">SERVER URL</label>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 text-xs font-mono bg-[#0A0A0A] border border-[#2A2A2A] text-[#00D4FF]">{mcpUrl}</code>
            <button onClick={() => copyToClipboard(mcpUrl, 'url')} className="px-2 py-2 text-xs bg-[#2A2A2A] text-[#F5F1E8]">
              {copied === 'url' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            </button>
          </div>
        </div>

        <div className="mb-3">
          <label className="text-[10px] font-mono text-[#A0A0A0] mb-1 block">BEARER TOKEN</label>
          <div className="flex items-center gap-2">
            <code className={`flex-1 px-3 py-2 text-xs font-mono bg-[#0A0A0A] border border-[#2A2A2A] ${displayApiKey ? 'text-[#F5F1E8]' : 'text-[#A0A0A0]'}`}>
              {displayApiKey || 'No active API key'}
            </code>
            {displayApiKey && (
              <button onClick={() => copyToClipboard(showKey || activeKey?.key_prefix || '', 'key')} className="px-2 py-2 text-xs bg-[#2A2A2A] text-[#F5F1E8]">
                {copied === 'key' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              </button>
            )}
          </div>
          {showKey && (
            <p className="text-[10px] mt-1 text-[#F2A900]">Save this key now — it cannot be retrieved again.</p>
          )}
        </div>

        {activeKey && (
          <div className="mb-3 p-2 bg-[#0A0A0A] border border-[#2A2A2A]">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono text-[#A0A0A0]">
                Created {new Date(activeKey.created_at).toLocaleDateString()}
              </span>
              <span className={`text-[10px] font-mono ${activeKey.expires_at ? 'text-[#F2A900]' : 'text-[#22C55E]'}`}>
                {activeKey.expires_at
                  ? `Expires ${new Date(activeKey.expires_at).toLocaleDateString()}`
                  : 'No expiration'}
              </span>
            </div>
            {activeKey.use_count > 0 && (
              <span className="text-[10px] font-mono text-[#A0A0A0]">
                Used {activeKey.use_count} time{activeKey.use_count !== 1 ? 's' : ''}
                {activeKey.last_used_at && ` — last ${new Date(activeKey.last_used_at).toLocaleDateString()}`}
              </span>
            )}
          </div>
        )}

        <div className="flex items-end gap-3">
          <div>
            <label className="text-[10px] font-mono text-[#A0A0A0] mb-1 block">EXPIRATION</label>
            <select
              value={expiration}
              onChange={e => setExpiration(e.target.value)}
              className="px-3 py-2 text-xs font-mono bg-[#0A0A0A] border border-[#2A2A2A] text-[#F5F1E8] min-h-[36px] appearance-none cursor-pointer"
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23A0A0A0' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', paddingRight: '28px' }}
              data-testid="mcp-key-expiration"
            >
              <option value="7d">7 days</option>
              <option value="30d">30 days</option>
              <option value="60d">60 days</option>
              <option value="90d">90 days</option>
              <option value="1y">1 year</option>
              <option value="never">No expiration</option>
            </select>
          </div>
          <button onClick={generateKey} disabled={generating}
            className={`px-4 py-2 min-h-[36px] text-xs font-medium flex items-center gap-1.5 disabled:opacity-50 ${activeKey ? 'bg-[#2A2A2A] text-[#F5F1E8]' : 'bg-[#FF4D00] text-[#0A0A0A]'}`}
            data-testid="mcp-generate-key-btn">
            {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            {activeKey ? 'Regenerate Key' : 'Generate API Key'}
          </button>
        </div>
        {expiration === 'never' && (
          <p className="text-[10px] mt-2 text-[#A61E17] font-mono">
            Tokens that don't expire pose a security risk if compromised. Consider using a shorter expiration and regenerating periodically.
          </p>
        )}
      </div>

      {/* ═══ PATH 1: MAGIC PROMPT (default, expanded) ═══ */}
      <div className="p-4 md:p-6 border bg-[#1E1E1E]" style={{ borderColor: 'rgba(255,77,0,0.3)' }} data-testid="magic-prompt-section">
        <div className="flex items-center gap-3 mb-1">
          <Zap className="w-5 h-5 shrink-0 text-[#FF4D00]" />
          <h3 className="text-lg font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#F5F1E8]">Magic Prompt</h3>
          <span className="text-[9px] font-mono px-2 py-0.5 font-bold uppercase tracking-wider bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/30">Recommended</span>
        </div>
        <p className="text-xs text-[#A0A0A0] mb-6 ml-8">Paste this prompt into Claude and it will configure everything automatically.</p>

        {!keyIsResolved && displayApiKey && (
          <div className="p-3 mb-4 bg-[#F2A900]/10 border border-[#F2A900]/30">
            <p className="text-xs text-[#F2A900]">
              <AlertCircle className="w-3 h-3 inline mr-1 -mt-0.5" />
              Your API key is masked. <strong>Regenerate Key</strong> above to get prompts with your full key embedded.
            </p>
          </div>
        )}

        {/* 6-step flow */}
        <div className="space-y-4">
          <McpStepBlock num={1} title="Copy the magic prompt" color="#FF4D00">
            <div className="relative">
              <pre className="text-xs font-mono text-[#E0E0E0] bg-[#0A0A0A] p-3 md:p-4 border border-[#2A2A2A] whitespace-pre-wrap max-h-40 overflow-y-auto">{magicPrompt}</pre>
              <button
                onClick={() => copyToClipboard(magicPrompt, 'magic-prompt')}
                className="absolute top-2 right-2 px-3 py-1.5 min-h-[44px] flex items-center gap-1.5 text-xs font-bold font-mono bg-[#FF4D00] text-[#0A0A0A] hover:bg-[#FF6B33] transition-colors"
                data-testid="mcp-copy-prompt">
                {copied === 'magic-prompt' ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy Prompt</>}
              </button>
            </div>
          </McpStepBlock>

          <McpStepBlock num={2} title="Paste into Claude Desktop or Claude Code" color="#FF4D00">
            <p className="text-xs text-[#A0A0A0]">Open a new conversation and paste the prompt. Claude will detect your OS, configure the MCP server, and download the Fray skill — all automatically in one go.</p>
          </McpStepBlock>

          <McpStepBlock num={3} title="Restart Claude" color="#F2A900">
            <p className="text-xs text-[#A0A0A0]">
              When Claude tells you setup is complete, fully quit Claude Desktop (<code className="font-mono text-[#F2A900]">Cmd+Q</code> on macOS, <code className="font-mono text-[#F2A900]">Alt+F4</code> on Windows — not just close the window) and relaunch it.
            </p>
          </McpStepBlock>

          <McpStepBlock num={4} title="Verify connection" color="#22C55E">
            <p className="text-xs text-[#A0A0A0] mb-3">After restarting, paste this into a <strong>new conversation</strong> to confirm everything works:</p>
            <div className="relative">
              <pre className="text-xs font-mono text-[#E0E0E0] bg-[#0A0A0A] p-3 border border-[#2A2A2A] whitespace-pre-wrap">{templates.mcp_verify_prompt || MCP_VERIFY_PROMPT}</pre>
              <button onClick={() => copyToClipboard(templates.mcp_verify_prompt || MCP_VERIFY_PROMPT, 'verify-prompt')}
                className="absolute top-2 right-2 px-3 py-1.5 flex items-center gap-1 text-xs font-mono font-bold bg-[#22C55E] text-[#0A0A0A] hover:bg-[#22C55E]/80"
                data-testid="mcp-copy-verify">
                {copied === 'verify-prompt' ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy Verify</>}
              </button>
            </div>
            <div className="mt-3 p-3 bg-[#00D4FF]/5 border border-[#00D4FF]/20">
              <p className="text-[10px] font-bold text-[#00D4FF] mb-1">Connection Lost?</p>
              <p className="text-[10px] text-[#A0A0A0]">Clear stale auth cache (<code className="font-mono text-[#00D4FF]">rm -rf ~/.mcp-auth</code>), restart Claude, and try again. Fray uses Streamable HTTP — each tool call is independent.</p>
            </div>
          </McpStepBlock>
        </div>

        <p className="text-[10px] text-[#A0A0A0] font-mono mt-4">
          Access level: <span className="text-[#FF4D00]">{readiness?.mcp_access_level || 'standard'}</span> — {readiness?.plan || user?.plan} plan
        </p>
      </div>

      {/* ═══ PATH 2: MANUAL SETUP (collapsed) ═══ */}
      <div className="border bg-[#1E1E1E] border-[#2A2A2A]" data-testid="manual-setup-section">
        <button
          onClick={() => setSetupPath(setupPath === 'manual' ? 'magic' : 'manual')}
          className="w-full p-4 flex items-center gap-3 text-left min-h-[44px]"
          data-testid="toggle-manual-setup">
          <Terminal className="w-5 h-5 shrink-0 text-[#A0A0A0]" />
          <span className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#A0A0A0] flex-1">Manual Setup</span>
          <span className="text-[10px] font-mono text-[#555] mr-2">Configure JSON manually</span>
          {setupPath === 'manual' ? <ChevronDown className="w-4 h-4 text-[#A0A0A0]" /> : <ChevronRight className="w-4 h-4 text-[#A0A0A0]" />}
        </button>

        {setupPath === 'manual' && (
          <div className="px-4 pb-4 md:px-6 md:pb-6">
            <div className="flex flex-wrap gap-2 mb-4">
              {MCP_PLATFORMS.map(p => (
                <button key={p.id} onClick={() => setPlatform(p.id)}
                  className={`px-3 py-2 min-h-[44px] text-xs font-mono border transition-colors ${
                    platform === p.id
                      ? 'text-[#FF4D00] border-[#FF4D00] bg-[#FF4D00]/10'
                      : 'text-[#F5F1E8] border-[#2A2A2A] hover:border-[#555]'
                  }`}
                  data-testid={`mcp-platform-${p.id}`}>
                  {p.label}
                </button>
              ))}
            </div>

            <div className="mb-3">
              <p className="text-xs text-[#A0A0A0]">
                {platform === 'claude-code'
                  ? 'Run this command in your terminal:'
                  : <>Add this to <code className="px-1 text-[#FF4D00] bg-[#FF4D00]/10">{getMcpConfigPath(platform, os)}</code>:</>}
              </p>
            </div>

            {MCP_PLATFORMS.find(p => p.id === platform)?.hasOsTabs && (
              <div className="flex gap-1 mb-3">
                {MCP_OS_TABS.map(o => (
                  <button key={o.id} onClick={() => setOs(o.id)}
                    className={`px-3 py-2 min-h-[44px] text-xs font-mono border transition-colors flex items-center gap-1.5 ${
                      os === o.id
                        ? 'text-[#FF4D00] border-[#FF4D00]/40 bg-[#FF4D00]/5'
                        : 'text-[#A0A0A0] border-[#2A2A2A] hover:text-[#F5F1E8]'
                    }`}
                    data-testid={`mcp-os-${o.id}`}>
                    <span>{o.icon}</span> {o.label}
                  </button>
                ))}
              </div>
            )}

            {platform === 'claude-desktop' && os === 'windows' && (
              <div className="p-3 mb-3 bg-[#F2A900]/10 border border-[#F2A900]/30">
                <p className="text-xs font-semibold text-[#F2A900] mb-1">Prerequisite:</p>
                <div className="relative">
                  <pre className="text-xs font-mono text-[#E0E0E0] bg-[#0A0A0A] p-2 border border-[#2A2A2A]">npm install -g mcp-remote</pre>
                  <button onClick={() => copyToClipboard('npm install -g mcp-remote', 'prereq')}
                    className="absolute top-1 right-1 px-2 py-1 text-[10px] font-mono bg-[#2A2A2A] text-[#F5F1E8]">
                    {copied === 'prereq' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  </button>
                </div>
              </div>
            )}

            <div className="relative">
              <pre className="text-xs font-mono text-[#E0E0E0] bg-[#0A0A0A] p-4 border border-[#2A2A2A] whitespace-pre-wrap overflow-x-auto">{configSnippet}</pre>
              <button onClick={() => copyToClipboard(configSnippet, 'config')}
                className="absolute top-2 right-2 px-2 py-1.5 flex items-center gap-1 text-[10px] font-mono bg-[#2A2A2A] text-[#F5F1E8]"
                data-testid="mcp-copy-config">
                {copied === 'config' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              </button>
            </div>

            {platform === 'claude-code' && (
              <div className="mt-4">
                <span className="text-sm font-semibold font-['Barlow_Condensed']">Authenticate</span>
                <p className="text-xs text-[#A0A0A0] mb-2">After adding the server, authenticate via the MCP panel:</p>
                <div className="relative mb-2">
                  <pre className="text-xs font-mono text-[#E0E0E0] bg-[#0A0A0A] p-3 border border-[#2A2A2A]">claude /mcp</pre>
                  <button onClick={() => copyToClipboard('claude /mcp', 'mcp-cmd')}
                    className="absolute top-1.5 right-1.5 px-2 py-1 text-[10px] font-mono bg-[#2A2A2A] text-[#F5F1E8]">
                    {copied === 'mcp-cmd' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  </button>
                </div>
                <p className="text-[10px] text-[#A0A0A0]">
                  Select the <code className="text-[#FF4D00]">fray</code> server, then Authenticate.
                </p>
              </div>
            )}

            <div className="mt-4 p-3 bg-[#F2A900]/5 border border-[#F2A900]/20">
              <p className="text-xs font-bold text-[#F2A900] mb-1">
                <RotateCcw className="w-3 h-3 inline mr-1" /> After adding the config, restart your app
              </p>
              <p className="text-[10px] text-[#A0A0A0]">Then paste the verify prompt from Step 5 above into a new conversation to confirm it's working.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function McpStepBlock({ num, title, color, children }) {
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <span className="w-7 h-7 flex items-center justify-center text-xs font-bold font-mono shrink-0" style={{ background: color, color: '#0A0A0A' }}>{num}</span>
        <div className="flex-1 w-px mt-1 bg-[#2A2A2A]" />
      </div>
      <div className="flex-1 pb-2">
        <p className="text-sm font-bold font-['Barlow_Condensed'] text-[#F5F1E8] mb-2">{title}</p>
        {children}
      </div>
    </div>
  );
}


function PlanTab({ user }) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    API.get('/api/billing/subscription')
      .then(r => setSubscription(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await API.post('/api/billing/portal-session', {
        return_url: window.location.href,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error('Failed to open billing portal:', err);
    }
    setPortalLoading(false);
  };

  if (loading) return <div className="animate-shimmer h-40" />;

  const plan = subscription?.plan || user?.plan || 'free';
  const isActive = subscription?.subscription_active;
  const billingState = subscription?.billing_state || 'active';

  return (
    <div data-testid="plan-tab">
      {subscription?.banner && <BillingBanner banner={subscription.banner} />}

      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] mb-6">
        <div className="flex items-start gap-3">
          <CreditCard className="w-5 h-5 text-[#FF4D00] shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-1">Subscription</h3>
            <p className="text-xs text-[#A0A0A0] leading-relaxed">
              Manage your Fray subscription, update payment methods, and view invoices.
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 bg-[#1A1A1A] border border-[#2A2A2A] mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="text-[10px] font-mono text-[#A0A0A0] block mb-1">CURRENT PLAN</span>
            <span className="text-lg font-bold font-['Barlow_Condensed'] capitalize" style={{ color: plan === 'free' ? '#A0A0A0' : '#FF4D00' }}>
              {plan}
            </span>
          </div>
          <div className="text-right">
            <span className="text-[10px] font-mono text-[#A0A0A0] block mb-1">STATUS</span>
            <span className={`text-xs font-mono px-2 py-1 border ${
              billingState === 'active' ? 'text-[#22C55E] border-[#22C55E]/30'
              : billingState === 'past_due_grace' ? 'text-[#F2A900] border-[#F2A900]/30'
              : billingState === 'past_due_expired' ? 'text-[#A61E17] border-[#A61E17]/30'
              : 'text-[#A0A0A0] border-[#2A2A2A]'
            }`}>
              {billingState === 'active' ? 'ACTIVE' :
               billingState === 'past_due_grace' ? 'PAST DUE' :
               billingState === 'past_due_expired' ? 'SUSPENDED' :
               billingState === 'canceled' ? 'CANCELED' : 'FREE'}
            </span>
          </div>
        </div>

        {subscription?.subscription_started && (
          <p className="text-xs text-[#A0A0A0] mb-4 font-mono">
            Member since {subscription.subscription_started?.slice(0, 10)}
          </p>
        )}

        {subscription?.read_only && (
          <div className="p-3 border border-[#A61E17] bg-[#A61E17]/10 mb-4">
            <p className="text-xs text-[#A61E17] font-mono">
              Your account is in read-only mode. Update your payment method to restore full access.
            </p>
          </div>
        )}

        {plan !== 'free' && (
          <button
            onClick={openBillingPortal}
            disabled={portalLoading}
            className="w-full px-4 py-3 bg-[#FF4D00] hover:bg-[#FF4D00]/80 text-[#F5F1E8] font-['Barlow_Condensed'] font-bold uppercase tracking-wider text-sm transition-colors disabled:opacity-50"
            data-testid="manage-subscription-btn"
          >
            {portalLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Opening Portal...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <ExternalLink className="w-4 h-4" /> Manage Subscription
              </span>
            )}
          </button>
        )}

        {plan === 'free' && (
          <a
            href="/pricing"
            className="block w-full px-4 py-3 bg-[#FF4D00] hover:bg-[#FF4D00]/80 text-[#F5F1E8] text-center font-['Barlow_Condensed'] font-bold uppercase tracking-wider text-sm transition-colors"
            data-testid="upgrade-btn"
          >
            Upgrade Plan
          </a>
        )}
      </div>

      <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
        <p className="text-xs text-[#A0A0A0] leading-relaxed">
          The billing portal lets you update your payment method, view invoices, change plans, or cancel your subscription.
          Powered by Stripe — Fray never stores your card details.
        </p>
      </div>
    </div>
  );
}


function UsageTab() {
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      API.get('/api/usage/summary').then(r => setSummary(r.data)),
      API.get('/api/usage/history').then(r => setHistory(r.data.frays || [])),
    ]).finally(() => setLoading(false));
  }, []);

  const handleExportCSV = async () => {
    const res = await API.get('/api/usage/export');
    const blob = new Blob([res.data.csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'fray-usage.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="animate-shimmer h-40" />;

  return (
    <div data-testid="usage-tab">
      {/* Monthly summary cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[
            { label: 'This Month', data: summary.current_month },
            { label: 'Last Month', data: summary.last_month },
            { label: 'All Time', data: summary.all_time },
          ].map((period, i) => (
            <div key={i} className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]">
              <span className="text-[10px] font-mono text-[#A0A0A0] block mb-2">{period.label}</span>
              <div className="text-2xl font-bold font-['Barlow_Condensed']">{period.data.frays}</div>
              <span className="text-[10px] text-[#A0A0A0]">Frays</span>
              <div className="mt-2 text-xs font-mono">
                <span className="text-[#22C55E]">{period.data.tokens?.toLocaleString()} tokens</span>
                {period.data.cost_usd > 0 && <span className="text-[#FF4D00] ml-2">${period.data.cost_usd.toFixed(4)}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* History table */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold font-['Barlow_Condensed']">Fray History</h3>
        <button onClick={handleExportCSV} className="px-3 py-1 text-[10px] font-mono border border-[#2A2A2A] text-[#A0A0A0] hover:text-[#FF4D00]">
          Export CSV
        </button>
      </div>
      {history.length === 0 ? (
        <p className="text-sm text-[#A0A0A0]">No usage data yet. Run a Fray to see stats.</p>
      ) : (
        <div className="border border-[#2A2A2A] overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#1A1A1A]">
              <tr>
                <th className="text-left p-2 font-mono text-[#A0A0A0]">Date</th>
                <th className="text-left p-2 font-mono text-[#A0A0A0]">Playbook</th>
                <th className="text-right p-2 font-mono text-[#A0A0A0]">Tokens In</th>
                <th className="text-right p-2 font-mono text-[#A0A0A0]">Tokens Out</th>
                <th className="text-right p-2 font-mono text-[#A0A0A0]">Cost</th>
                <th className="text-right p-2 font-mono text-[#A0A0A0]">Status</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i} className="border-t border-[#2A2A2A]">
                  <td className="p-2 font-mono">{h.date?.slice(0, 10)}</td>
                  <td className="p-2">{h.playbook_name}</td>
                  <td className="p-2 text-right font-mono">{h.tokens_in?.toLocaleString()}</td>
                  <td className="p-2 text-right font-mono">{h.tokens_out?.toLocaleString()}</td>
                  <td className="p-2 text-right font-mono text-[#22C55E]">${h.cost_usd?.toFixed(4)}</td>
                  <td className="p-2 text-right">
                    <span className={`text-[9px] px-1.5 py-0.5 font-mono ${h.status === 'completed' ? 'text-[#22C55E]' : 'text-[#A0A0A0]'}`}>{h.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// #476 — Available Models Tab (read-only, governance-aware)
function AvailableModelsTab({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tierTab, setTierTab] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    API.get('/api/models/available')
      .then(r => setData(r.data))
      .catch(() => setData({ models: [], governance: { enabled: false }, total: 0, warning: 'Failed to load models' }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-xs text-[#A0A0A0] py-8">Loading available models...</div>;
  if (!data) return null;

  const { models, governance } = data;
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'enterprise';

  // Tier counts
  const tierCounts = {};
  models.forEach(m => { tierCounts[m.tier] = (tierCounts[m.tier] || 0) + 1; });

  // Filtered
  const filtered = models.filter(m => {
    if (tierTab && m.tier !== tierTab) return false;
    if (search) {
      const s = search.toLowerCase();
      return (m.slug || '').toLowerCase().includes(s) ||
             (m.display_name || '').toLowerCase().includes(s) ||
             (m.provider || '').toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div className="space-y-4" data-testid="available-models-tab">
      {/* Header */}
      <div className="p-3 bg-[#0F0F0F] border border-[#2A2A2A]">
        <p className="text-sm font-medium">
          {governance.enabled
            ? `Your workspace admin has approved ${models.length} models for use`
            : `${models.length} models available on your plan`}
        </p>
        {governance.enabled && governance.workspace_name && (
          <p className="text-xs text-[#A0A0A0] mt-1">Workspace: {governance.workspace_name}</p>
        )}
        {governance.deprecated_count > 0 && (
          <p className="text-xs text-[#F2A900] mt-1">⚠ {governance.deprecated_count} deprecated</p>
        )}
        {isAdmin && (
          <a href={governance.enabled ? '/enterprise' : '/admin'} className="text-xs text-[#00D4FF] hover:underline mt-2 inline-block">
            Manage models →
          </a>
        )}
      </div>

      {data.warning && (
        <p className="text-xs text-[#F2A900] p-2 bg-[#1A1A1A] border border-[#F2A900]">{data.warning}</p>
      )}

      {/* Tier tabs */}
      <div className="flex gap-1">
        <button onClick={() => setTierTab('')}
          className={`px-2.5 py-1 text-[10px] font-mono transition-colors ${
            !tierTab ? 'bg-[#FF4D00] text-white' : 'bg-[#0F0F0F] border border-[#2A2A2A] text-[#A0A0A0]'
          }`}>All ({models.length})</button>
        {TIER_ORDER.filter(t => tierCounts[t]).map(t => (
          <button key={t} onClick={() => setTierTab(tierTab === t ? '' : t)}
            className={`px-2.5 py-1 text-[10px] font-mono transition-colors ${
              tierTab === t ? 'text-white' : 'bg-[#0F0F0F] border border-[#2A2A2A] text-[#A0A0A0]'
            }`}
            style={tierTab === t ? { backgroundColor: TIER_COLORS[t] } : {}}>
            {t.charAt(0).toUpperCase() + t.slice(1)} ({tierCounts[t]})
          </button>
        ))}
      </div>

      {/* Search */}
      <input value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search models..." className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs font-mono focus:outline-none focus:border-[#00D4FF]" />

      {/* Model list (read-only) */}
      <div className="space-y-1 max-h-80 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-xs text-[#A0A0A0] py-4">{models.length === 0 ? 'No models available.' : 'No models match your search.'}</p>
        ) : filtered.map(m => (
          <div key={m.slug} className="flex items-center gap-2 p-2 bg-[#0F0F0F] border border-[#2A2A2A]">
            <span className="text-xs font-mono flex-1 truncate">{m.display_name || m.slug}</span>
            <span className="text-[8px] font-mono px-1.5 py-0.5 border" style={{
              color: TIER_COLORS[m.tier] || '#A0A0A0',
              borderColor: (TIER_COLORS[m.tier] || '#A0A0A0') + '40'
            }}>{m.tier}</span>
            {m.supports_thinking && <span className="text-[8px] text-[#F2A900]" title="Thinking">T</span>}
            {m.supports_function_calling && <span className="text-[8px] text-[#00D4FF]" title="Tools">F</span>}
            {m.supports_images && <span className="text-[8px] text-[#FF4D00]" title="Images">I</span>}
            {m.context_window && <span className="text-[8px] font-mono text-[#A0A0A0]">{Math.round(m.context_window/1000)}k</span>}
            {m.deprecated && <span className="text-[8px] text-[#F2A900]">⚠</span>}
          </div>
        ))}
      </div>
    </div>
  );
}


// #196 + #198 — GDPR Privacy Tab
function PrivacyTab({ user }) {
  const [exports, setExports] = useState([]);
  const [deletion, setDeletion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadPrivacyData();
  }, []);

  const loadPrivacyData = async () => {
    try {
      const [expRes, delRes] = await Promise.all([
        API.get('/api/me/data-export').catch(() => ({ data: { exports: [] } })),
        API.get('/api/me/delete-account').catch(() => ({ data: { deletion: null } })),
      ]);
      setExports(expRes.data.exports || []);
      setDeletion(delRes.data.deletion);
    } catch {}
    setLoading(false);
  };

  const requestExport = async () => {
    setExporting(true);
    setError('');
    try {
      const res = await API.post('/api/me/data-export');
      setSuccess('Data export created successfully.');
      await loadPrivacyData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Export failed');
    }
    setExporting(false);
  };

  const downloadExport = async (exportId) => {
    try {
      const res = await API.get(`/api/me/data-export/${exportId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `fray-data-export-${exportId.slice(0, 8)}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Download failed');
    }
  };

  const requestDeletion = async () => {
    if (!deletePassword) { setError('Password required'); return; }
    setDeleting(true);
    setError('');
    try {
      const res = await API.post('/api/me/delete-account', { password: deletePassword });
      setSuccess(`Account deletion scheduled for ${new Date(res.data.scheduled_at).toLocaleDateString()}`);
      setShowDeleteConfirm(false);
      setDeletePassword('');
      await loadPrivacyData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Deletion request failed');
    }
    setDeleting(false);
  };

  const cancelDeletion = async () => {
    if (!deletion) return;
    try {
      await API.post('/api/me/cancel-deletion', { cancel_token: deletion.cancel_token || '' });
      setSuccess('Account deletion cancelled.');
      setDeletion(null);
      await loadPrivacyData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Cancellation failed');
    }
  };

  if (loading) return <div className="text-center py-8 text-[#A0A0A0]"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6">
      {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-400">{error}</div>}
      {success && <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-sm text-green-400">{success}</div>}

      {/* Data Export */}
      <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-4 md:p-6">
        <div className="flex items-center gap-2 mb-3">
          <FileArchive className="w-4 h-4 text-[#3B82F6]" />
          <h3 className="font-['Barlow_Condensed'] font-semibold">Data Export</h3>
        </div>
        <p className="text-xs text-[#A0A0A0] mb-4">Download all your personal data (GDPR Article 20). Export includes your profile, frays, transcripts, and usage history.</p>
        <button
          onClick={requestExport}
          disabled={exporting}
          className="px-4 py-2 min-h-[44px] bg-[#3B82F6] text-white text-sm font-medium rounded-lg hover:bg-[#3B82F6]/80 disabled:opacity-50 transition-colors"
        >
          {exporting ? 'Creating export...' : 'Export My Data'}
        </button>

        {exports.length > 0 && (
          <div className="mt-4 space-y-2">
            <h4 className="text-xs font-mono text-[#A0A0A0] uppercase">Previous Exports</h4>
            {exports.map(exp => (
              <div key={exp.id} className="flex items-center justify-between bg-[#0F0F0F] rounded p-3">
                <div>
                  <span className="text-xs font-mono">{new Date(exp.created_at).toLocaleDateString()}</span>
                  <span className={`ml-2 text-[9px] px-1.5 py-0.5 rounded font-mono ${
                    exp.status === 'ready' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                  }`}>{exp.status}</span>
                  {exp.data_size && <span className="ml-2 text-[9px] text-[#A0A0A0]">{(exp.data_size / 1024).toFixed(1)} KB</span>}
                </div>
                {exp.status === 'ready' && (
                  <button onClick={() => downloadExport(exp.id)} className="text-xs text-[#3B82F6] hover:underline flex items-center gap-1 min-h-[44px]">
                    <Download className="w-3 h-3" /> Download
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Account Deletion */}
      <div className="bg-[#1A1A1A] border border-red-500/20 rounded-lg p-4 md:p-6">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <h3 className="font-['Barlow_Condensed'] font-semibold text-red-400">Delete Account</h3>
        </div>
        <p className="text-xs text-[#A0A0A0] mb-4">
          Permanently delete your account and all associated data (GDPR Article 17).
          A 30-day grace period applies — you can cancel during this time.
        </p>

        {deletion ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <p className="text-sm text-red-300 mb-2">
              Account deletion scheduled for <strong>{new Date(deletion.scheduled_at).toLocaleDateString()}</strong>
            </p>
            <button
              onClick={cancelDeletion}
              className="px-4 py-2 min-h-[44px] bg-[#2A2A2A] text-white text-sm font-medium rounded-lg hover:bg-[#3A3A3A] transition-colors"
            >
              Cancel Deletion
            </button>
          </div>
        ) : showDeleteConfirm ? (
          <div className="space-y-3">
            <p className="text-xs text-red-300">Enter your password to confirm account deletion:</p>
            <input
              type="password"
              value={deletePassword}
              onChange={e => setDeletePassword(e.target.value)}
              placeholder="Your password"
              className="w-full px-3 py-2 min-h-[44px] bg-[#0F0F0F] border border-[#2A2A2A] rounded text-sm"
            />
            <div className="flex gap-2">
              <button
                onClick={requestDeletion}
                disabled={deleting}
                className="px-4 py-2 min-h-[44px] bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Processing...' : 'Confirm Deletion'}
              </button>
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); }}
                className="px-4 py-2 min-h-[44px] bg-[#2A2A2A] text-white text-sm rounded-lg hover:bg-[#3A3A3A]"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2 min-h-[44px] bg-red-600/20 text-red-400 text-sm font-medium rounded-lg hover:bg-red-600/30 border border-red-500/30 transition-colors"
          >
            Delete My Account
          </button>
        )}
      </div>
    </div>
  );
}
